import React, { useMemo, useState } from "react";
import theme from "../../../styles/theme";
import InfoPanel from "../../reusable/Forms/InfoPanel";

/* ============================================================================
   SpecificationReviewTab.jsx
   ----------------------------------------------------------------------------
   ONGLET REVIEW — PANNEAU GAUCHE (A1)

   RÔLE
   - faire apparaître les sujets qui nécessitent une décision
   - distinguer les verrous, les tensions et les points de couverture à traiter
   - préparer la consolidation projet sans surcharger la lecture

   STRUCTURE
   1. Synthèse dossier
   2. Points à traiter + loupe décisionnelle
   3. Zones de tension transverses

   NOTE
   - données mockées
   - structure prête pour raccordement backend / PostgreSQL
============================================================================ */

/* ============================================================================
   TOKENS
============================================================================ */

const T = {
  border: theme?.colors?.borders?.default || "#d6dee4",
  borderStrong: theme?.colors?.borders?.darkblue || "#b2c1c7",
  hover: theme?.colors?.background?.hover || "#f7f9fc",
  panel: theme?.colors?.background?.panel || "#f1f3f6",

  text: theme?.colors?.text?.primary || "#164D63",
  body: theme?.colors?.text?.body || "#3F5D6E",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",

  corelia: theme?.colors?.text?.corelia || "#0e3f56",
  caneva: theme?.colors?.text?.caneva || "#c24c2c",
  green: theme?.colors?.text?.green || "#1A8C74",
  orange: theme?.colors?.text?.secondary || "#B8621B",

  bluePastel: theme?.Generic?.blue?.pastel?.bg || "#E8F2F6",
  greenPastel: theme?.Generic?.green?.pastel?.bg || "#E7F3ED",
  yellowPastel: theme?.Generic?.yellow?.pastel?.bg || "#F9F3E3",
  orangePastel: theme?.Generic?.orange?.pastel?.bg || "#F8ECE5",
  greyPastel: theme?.Generic?.grey?.pastel?.bg || "#EDF1F4",

  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  orangeText: theme?.Generic?.orange?.pastel?.text || "#9A4315",
  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",

  focusLine: "#6B7C8F",
};

const SCALE = 1.06;

const ICONS = {
  target: "/assets/icones/container/target.svg",
  assess: "/assets/icones/container/search-check.svg",
  sight: "/assets/icones/container/crosshair.svg",
};

/* ============================================================================
   DATA MOCK
============================================================================ */

const REVIEW_ITEMS = [
  {
    id: "rv-001",
    title: "Alimentation secourue des automatismes à confirmer",
    perimeter: "Station de pompage — Automatisme — Lot Électrique",
    maturityStatus: "À consolider",
    technicalStatus: "Instable",
    projectImpact: "Bloquant",
    issueType: "Décision",
    impact:
      "L’absence de schéma de secours stabilisé fragilise la continuité de service et reporte l’estimation fiable du système.",
    decision:
      "Valider le principe de secours, le niveau d’autonomie et l’architecture retenue.",
  },
  {
    id: "rv-002",
    title: "Interface pompe / armoire non stabilisée",
    perimeter: "Station de pompage — Commande — Hydraulique / Électricité",
    maturityStatus: "En cours",
    technicalStatus: "Instable",
    projectImpact: "Critique",
    issueType: "Interface",
    impact:
      "Le dialogue entre équipement et armoire reste ambigu, avec effet potentiel sur la logique d’exploitation et la cohérence des signaux.",
    decision:
      "Arbitrer les échanges commande / retour d’état et figer les responsabilités d’interface.",
  },
  {
    id: "rv-003",
    title: "Drainage périphérique du réservoir non spécifié",
    perimeter: "Réservoir — Drainage — Lot Génie civil",
    maturityStatus: "Non traitée",
    technicalStatus: "Non définie",
    projectImpact: "Critique",
    issueType: "Couverture",
    impact:
      "Une partie de la fonction de protection ouvrage reste non couverte, avec exposition durable sur l’ouvrage et son environnement.",
    decision:
      "Formuler la spécification de drainage et confirmer son niveau d’exigence projet.",
  },
  {
    id: "rv-004",
    title: "Réservation de traversée de dalle à verrouiller",
    perimeter: "Bâtiment technique — Interfaces GC — Lot Génie civil",
    maturityStatus: "En cours",
    technicalStatus: "Partielle",
    projectImpact: "Critique",
    issueType: "Coordination",
    impact:
      "La coordination inter-lots reste incomplète et peut générer des reprises tardives.",
    decision:
      "Confirmer les passages retenus et figer l’hypothèse de coordination.",
  },
  {
    id: "rv-005",
    title: "Protection anticorrosion interne à harmoniser",
    perimeter: "Réservoir — Protection structurelle — Lot Structure",
    maturityStatus: "À consolider",
    technicalStatus: "Partielle",
    projectImpact: "Sous contrôle",
    issueType: "Cohérence",
    impact:
      "Le sujet reste techniquement exploitable mais des références doivent être alignées avant validation finale.",
    decision:
      "Consolider le référentiel technique et valider la formulation définitive.",
  },
];

/* ----------------------------------------------------------------------------
   Volume mock augmenté uniquement pour tester le scroll de la colonne
---------------------------------------------------------------------------- */

const REVIEW_ITEMS_EXTENDED = [
  ...REVIEW_ITEMS,
  ...REVIEW_ITEMS.map((item, index) => ({
    ...item,
    id: `${item.id}-a-${index}`,
  })),
  ...REVIEW_ITEMS.map((item, index) => ({
    ...item,
    id: `${item.id}-b-${index}`,
  })),
];

const TENSION_ZONES = [
  {
    id: "if-1",
    label: "Hydraulique / Électricité",
    detail: "2 sujets critiques encore ouverts sur la chaîne commande / secours.",
    level: "Critique",
    system: "Pompage / Commande",
  },
  {
    id: "if-2",
    label: "Génie civil / Hydraulique",
    detail: "Réservations et drainage encore partiellement cadrés.",
    level: "Critique",
    system: "Réservoir / Interfaces GC",
  },
  {
    id: "if-3",
    label: "Exploitation / Maintenance",
    detail: "Logique d’usage convergente mais encore incomplète sur certains accès.",
    level: "Sous contrôle",
    system: "Bâtiment technique / Exploitation",
  },
  {
    id: "if-4",
    label: "Électricité / Automatisme",
    detail: "Schéma de secours à confirmer avant consolidation.",
    level: "Critique",
    system: "Automatisme / Secours énergie",
  },
];

/* ============================================================================
   HELPERS
============================================================================ */

const getImpactBarColor = (value) => {
  if (value === "Bloquant") return T.caneva;
  if (value === "Critique") return T.orange;
  if (value === "Sous contrôle") return T.corelia;
  return T.borderStrong;
};

const getMaturityPillStyle = (value) => {
  const map = {
    "Non traitée": { bg: T.greyPastel, text: T.greyText },
    "En cours": { bg: T.bluePastel, text: T.blueText },
    "À consolider": { bg: T.yellowPastel, text: T.yellowText },
    Validée: { bg: T.greenPastel, text: T.greenText },
  };
  return map[value] || { bg: T.greyPastel, text: T.greyText };
};

const getTechnicalPillStyle = (value) => {
  const map = {
    "Non définie": { bg: T.greyPastel, text: T.greyText },
    Instable: { bg: T.orangePastel, text: T.orangeText },
    Partielle: { bg: T.yellowPastel, text: T.yellowText },
    "Maîtrisée": { bg: T.greenPastel, text: T.greenText },
  };
  return map[value] || { bg: T.greyPastel, text: T.greyText };
};

const getImpactTextColor = (value) => {
  const map = {
    Bloquant: T.caneva,
    Critique: T.orange,
    "Sous contrôle": T.corelia,
    Neutre: T.soft,
  };
  return map[value] || T.soft;
};

const filterTensions = (items, filter) => {
  if (filter === "Toutes") return items;
  if (filter === "Critiques") {
    return items.filter((i) => i.level === "Critique");
  }
  if (filter === "Sous contrôle") {
    return items.filter((i) => i.level === "Sous contrôle");
  }

  return items.filter(
    (i) =>
      i.label.toLowerCase().includes(filter.toLowerCase()) ||
      i.system.toLowerCase().includes(filter.toLowerCase())
  );
};

/* ============================================================================
   UI ATOMS
============================================================================ */

const TitleWithIcon = ({ icon, children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <img src={icon} alt="" style={{ width: 14, opacity: 0.7 }} />
    <span>{children}</span>
  </div>
);

const SectionTitle = ({ icon, children }) => (
  <div
    style={{
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.79rem",
      fontWeight: theme?.fontWeights?.medium || 500,
      lineHeight: 1.2,
      color: T.soft,
      marginBottom: 10,
      paddingBottom: 6,
      borderBottom: `1px solid ${T.border}`,
    }}
  >
    <TitleWithIcon icon={icon}>{children}</TitleWithIcon>
  </div>
);

const MicroTag = ({ label, bg, text }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 8px",
      borderRadius: theme?.borders?.radius?.full || 9999,
      background: bg,
      color: text,
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.69rem",
      lineHeight: 1,
      fontWeight: 500,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </div>
);

/* ============================================================================
   COMPONENT
============================================================================ */

const SpecificationReviewTab = () => {
  const [selectedId, setSelectedId] = useState(REVIEW_ITEMS_EXTENDED[0].id);
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("Toutes");

  const selectedItem = useMemo(
    () => REVIEW_ITEMS_EXTENDED.find((i) => i.id === selectedId),
    [selectedId]
  );

  const filteredTensions = useMemo(
    () => filterTensions(TENSION_ZONES, selectedFilter),
    [selectedFilter]
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 44,
      }}
    >
      {/* ============================================================
         SYNTHÈSE DOSSIER
      ============================================================ */}
      {/* <InfoPanel
        variant="blue"
        title="Dossier encore sous réserve"
        message="Des verrous critiques subsistent sur la continuité de service, certaines interfaces et une partie de la couverture fonctionnelle."
      /> */}

      {/* ============================================================
         ZONE CENTRALE — POINTS À TRAITER + LOUPE
      ============================================================ */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0,48%) minmax(0,1fr)",
          gap: 38,
        }}
      >
        {/* --------------------------------------------------------
           POINTS À TRAITER
        -------------------------------------------------------- */}
        <div style={{ minWidth: 0 }}>
          <SectionTitle icon={ICONS.target}>Points à traiter</SectionTitle>

          <div
            style={{
              maxHeight: 420,
              overflowY: "auto",
              overflowX: "hidden",
              paddingRight: 4,
            }}
          >
            {REVIEW_ITEMS_EXTENDED.map((item) => {
              const selected = selectedId === item.id;
              const hovered = hoveredId === item.id;

              const maturityStyle = getMaturityPillStyle(item.maturityStatus);
              const technicalStyle = getTechnicalPillStyle(item.technicalStatus);

              return (
                <div
                  key={item.id}
                  onClick={() => setSelectedId(item.id)}
                  onMouseEnter={() => setHoveredId(item.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "4px 1fr auto 6px",
                    gap: 12,
                    padding: "12px 0",
                    borderBottom: `1px solid ${T.border}`,
                    background: !selected && hovered ? T.hover : "transparent",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      background: getImpactBarColor(item.projectImpact),
                    }}
                  />

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: theme?.fonts?.body || "inherit",
                        fontSize: "0.84rem",
                        fontWeight: 500,
                        color: T.text,
                        lineHeight: 1.2,
                      }}
                    >
                      {item.title}
                    </div>

                    <div
                      style={{
                        fontFamily: theme?.fonts?.body || "inherit",
                        fontSize: "0.73rem",
                        color: T.body,
                        lineHeight: 1.2,
                      }}
                    >
                      {item.perimeter}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 6,
                        alignItems: "center",
                      }}
                    >
                      <MicroTag
                        label={item.maturityStatus}
                        bg={maturityStyle.bg}
                        text={maturityStyle.text}
                      />
                      <MicroTag
                        label={item.technicalStatus}
                        bg={technicalStyle.bg}
                        text={technicalStyle.text}
                      />
                      <div
                        style={{
                          fontFamily: theme?.fonts?.body || "inherit",
                          fontSize: "0.70rem",
                          lineHeight: 1.1,
                          color: getImpactTextColor(item.projectImpact),
                          fontWeight: 500,
                        }}
                      >
                        {item.projectImpact}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "flex-end",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: theme?.fonts?.body || "inherit",
                        fontSize: "0.69rem",
                        lineHeight: 1.1,
                        color: T.verySoft,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.issueType}
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        width: 4,
                        height: 24,
                        background: selected ? T.focusLine : "transparent",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* --------------------------------------------------------
           LOUPE DÉCISIONNELLE
        -------------------------------------------------------- */}
        <div style={{ minWidth: 0 }}>
          <SectionTitle icon={ICONS.assess}>Décision à préparer</SectionTitle>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            <div
              style={{
                width: 4,
                height: 22,
                background: T.focusLine,
              }}
            />

            <div
              style={{
                fontFamily: theme?.fonts?.body || "inherit",
                fontSize: "0.98rem",
                fontWeight: 500,
                color: T.text,
                lineHeight: 1.2,
              }}
            >
              {selectedItem.title}
            </div>

            <div
              style={{
                height: 1,
                background: T.border,
              }}
            />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 10,
              }}
            >
              {[
                { label: "Périmètre", value: selectedItem.perimeter },
                { label: "Nature", value: selectedItem.issueType },
                { label: "Avancement", value: selectedItem.maturityStatus },
                { label: "Maîtrise", value: selectedItem.technicalStatus },
                { label: "Impact projet", value: selectedItem.projectImpact },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                >
                  <div
                    style={{
                      fontFamily: theme?.fonts?.body || "inherit",
                      fontSize: "0.71rem",
                      color: T.soft,
                      lineHeight: 1.1,
                    }}
                  >
                    {item.label}
                  </div>

                  <div
                    style={{
                      fontFamily: theme?.fonts?.body || "inherit",
                      fontSize: "0.78rem",
                      color: T.body,
                      lineHeight: 1.2,
                    }}
                  >
                    {item.value}
                  </div>
                </div>
              ))}
            </div>

            <div>
              <div
                style={{
                  fontFamily: theme?.fonts?.body || "inherit",
                  fontSize: "0.71rem",
                  color: T.soft,
                  lineHeight: 1.1,
                  marginBottom: 6,
                }}
              >
                Impact projet
              </div>

              <div
                style={{
                  fontFamily: theme?.fonts?.body || "inherit",
                  fontSize: "0.81rem",
                  color: T.body,
                  lineHeight: 1.45,
                }}
              >
                {selectedItem.impact}
              </div>
            </div>

            <div>
              <div
                style={{
                  fontFamily: theme?.fonts?.body || "inherit",
                  fontSize: "0.71rem",
                  color: T.soft,
                  lineHeight: 1.1,
                  marginBottom: 6,
                }}
              >
                Décision attendue
              </div>

              <div
                style={{
                  fontFamily: theme?.fonts?.body || "inherit",
                  fontSize: "0.81rem",
                  color: T.caneva,
                  lineHeight: 1.45,
                }}
              >
                {selectedItem.decision}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================
         ZONES DE TENSION
      ============================================================ */}
      <div>
        <SectionTitle icon={ICONS.sight}>Zones de tension</SectionTitle>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 12,
          }}
        >
          {[
            "Toutes",
            "Critiques",
            "Sous contrôle",
            "Hydraulique",
            "Électricité",
            "Génie civil",
          ].map((filter) => {
            const active = selectedFilter === filter;

            return (
              <button
                key={filter}
                type="button"
                onClick={() => setSelectedFilter(filter)}
                style={{
                  fontFamily: theme?.fonts?.body || "inherit",
                  fontSize: "0.69rem",
                  padding: "5px 8px",
                  borderRadius: theme?.borders?.radius?.lg || 6,
                  border: `1px solid ${active ? T.corelia : T.border}`,
                  background: active ? T.bluePastel : "#ffffff",
                  color: active ? T.corelia : T.body,
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                {filter}
              </button>
            );
          })}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, 230px)",
            gap: 12,
            justifyContent: "start",
          }}
        >
          {filteredTensions.map((item) => {
            const impactColor =
              item.level === "Critique" ? T.orangeText : T.corelia;
            const impactBg =
              item.level === "Critique" ? T.orangePastel : T.bluePastel;

            return (
              <div
                key={item.id}
                style={{
                  border: `1px solid ${T.border}`,
                  padding: 10,
                  minHeight: 84,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  background: "#ffffff",
                }}
              >
                <div
                  style={{
                    fontFamily: theme?.fonts?.body || "inherit",
                    fontSize: "0.78rem",
                    fontWeight: 500,
                    color: T.text,
                    lineHeight: 1.2,
                  }}
                >
                  {item.label}
                </div>

                <div
                  style={{
                    fontFamily: theme?.fonts?.body || "inherit",
                    fontSize: "0.70rem",
                    color: T.verySoft,
                    lineHeight: 1.1,
                  }}
                >
                  {item.system}
                </div>

                <div
                  style={{
                    fontFamily: theme?.fonts?.body || "inherit",
                    fontSize: "0.72rem",
                    color: T.body,
                    lineHeight: 1.35,
                    flexGrow: 1,
                  }}
                >
                  {item.detail}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <div
                    style={{
                      fontFamily: theme?.fonts?.body || "inherit",
                      fontSize: "0.67rem",
                      padding: "4px 8px",
                      borderRadius: theme?.borders?.radius?.full || 9999,
                      background: impactBg,
                      color: impactColor,
                      fontWeight: 500,
                    }}
                  >
                    {item.level}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SpecificationReviewTab;